import { z } from "zod";
import { CREDIT_PACKS, creditPackSchema, creditPacks } from "./checkout";
import { contentDigestSchema } from "./tools";

/**
 * Operator roles. `admin` and `operator` are people who may act; `read-only` may only observe.
 * Administrator is a product role, not a path to a secret.
 */
export const operatorRoles = ["admin", "operator", "read-only"] as const;
export const operatorRoleSchema = z.enum(operatorRoles);
export type OperatorRole = z.infer<typeof operatorRoleSchema>;

export const operatorSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  email: z.string().min(1),
  role: operatorRoleSchema,
  lastGoogleMfaAt: z.string().datetime().nullable(),
});
export type Operator = z.infer<typeof operatorSchema>;

/** The session carries no token and cannot represent a project audience. */
export const operatorSessionStateSchema = z
  .object({
    authenticated: z.boolean(),
    audience: z.literal("operator"),
    operator: operatorSchema.nullable(),
    breakGlass: z.boolean(),
    googleMfaVerifiedAt: z.string().datetime().nullable(),
  })
  .refine(
    (value) =>
      value.authenticated === (value.operator !== null) &&
      value.authenticated === (value.googleMfaVerifiedAt !== null),
    { message: "An authenticated operator session requires a verified Google MFA event." },
  );
export type OperatorSessionState = z.infer<typeof operatorSessionStateSchema>;

export const operatorListSchema = z.array(operatorSchema);
export type OperatorList = z.infer<typeof operatorListSchema>;

/**
 * `POST /api/v1/operator/access` provisions an operator account. Google identity links on the
 * first verified sign-in; the request never claims that MFA has already happened.
 */
export const addOperatorSchema = z
  .object({
    email: z.string().email().max(320),
    role: operatorRoleSchema,
  })
  .strict();
export type AddOperator = z.infer<typeof addOperatorSchema>;

/** A service principal and the one action it may perform. It carries no secret. */
export const servicePrincipalSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  allowedAction: z.string().min(1),
  lastRotatedAt: z.string().datetime().nullable(),
});
export type ServicePrincipal = z.infer<typeof servicePrincipalSchema>;

export const servicePrincipalListSchema = z.array(servicePrincipalSchema);
export type ServicePrincipalList = z.infer<typeof servicePrincipalListSchema>;

/**
 * Dependency health. A probe that has gone quiet is `unknown` with its age, never the last
 * good answer, so a stale probe cannot read as healthy.
 */
export const dependencyStates = ["healthy", "degraded", "down", "unknown"] as const;
export const dependencyStateSchema = z.enum(dependencyStates);
export type DependencyState = z.infer<typeof dependencyStateSchema>;

export const dependencyHealthSchema = z.object({
  name: z.string().min(1),
  state: dependencyStateSchema,
  latencyMs: z.number().int().min(0).nullable(),
  lastProbedAt: z.string().datetime().nullable(),
  unknownAgeSeconds: z.number().int().min(0).nullable(),
});
export type DependencyHealth = z.infer<typeof dependencyHealthSchema>;

export const operatorHealthSchema = z.object({
  dependencies: z.array(dependencyHealthSchema),
  checkedAt: z.string().datetime(),
});
export type OperatorHealth = z.infer<typeof operatorHealthSchema>;

/**
 * One tenant as the operator list shows it: counts and states only, never project content.
 */
export const tenantSummarySchema = z.object({
  projectId: z.uuid(),
  state: z.enum(["active", "archived", "deletion-requested"]),
  memberCount: z.number().int().min(0),
  runsLastSevenDays: z.number().int().min(0),
  creditBalance: z.number().int(),
  needsAttention: z.boolean(),
});
export type TenantSummary = z.infer<typeof tenantSummarySchema>;

export const tenantListSchema = z.array(tenantSummarySchema);
export type TenantList = z.infer<typeof tenantListSchema>;

/** Worker states, as the fleet page badges them. */
export const workerStates = ["healthy", "draining", "stale", "incompatible"] as const;
export const workerStateSchema = z.enum(workerStates);
export type WorkerState = z.infer<typeof workerStateSchema>;

/**
 * One worker. A stale or incompatible worker advertises no usable capacity and is excluded from
 * the total, so the fleet cannot look healthier than it is.
 */
export const workerSchema = z.object({
  id: z.uuid(),
  version: z.string().min(1),
  heartbeatAgeSeconds: z.number().int().min(0),
  slots: z.number().int().min(0),
  usableSlots: z.number().int().min(0),
  state: workerStateSchema,
});
export type Worker = z.infer<typeof workerSchema>;

export const workerListSchema = z.object({
  workers: z.array(workerSchema),
  totalUsableSlots: z.number().int().min(0),
});
export type WorkerList = z.infer<typeof workerListSchema>;

/** Worker heartbeats are authenticated by their service identity, not a caller-supplied id. */
export const workerHeartbeatSchema = z.object({
  version: z.string().trim().min(1),
  slots: z.number().int().min(0),
});
export type WorkerHeartbeat = z.infer<typeof workerHeartbeatSchema>;

/** A worker must stop taking new leases when `drainRequested` is true. */
export const workerHeartbeatReceiptSchema = z.object({
  workerId: z.uuid(),
  drainRequested: z.boolean(),
});
export type WorkerHeartbeatReceipt = z.infer<typeof workerHeartbeatReceiptSchema>;

/** A drain request stops new work and leaves leased runs in place for reconciliation. */
export const workerDrainSchema = z.object({
  workerId: z.uuid(),
  state: z.literal("draining"),
  activeLeases: z.number().int().min(0),
  reconciliationRequired: z.number().int().min(0),
});
export type WorkerDrain = z.infer<typeof workerDrainSchema>;

/** Typed repair resolutions. `keep-unknown` leaves an unknown outcome visible until reconciled. */
export const repairResolutions = [
  "confirm-took-effect",
  "confirm-did-not",
  "retry-same-key",
  "keep-unknown",
] as const;
export const repairResolutionSchema = z.enum(repairResolutions);
export type RepairResolution = z.infer<typeof repairResolutionSchema>;

export const repairStates = [
  "awaiting-proposal",
  "proposed",
  "authorised",
  "executing",
  "applied",
  "rejected",
] as const;
export const repairStateSchema = z.enum(repairStates);
export type RepairState = z.infer<typeof repairStateSchema>;

/** An immutable remote observation captured by the reconciler for one run revision. */
export const repairRemoteOutcomes = ["took-effect", "did-not-take-effect", "inconclusive"] as const;
export const repairRemoteOutcomeSchema = z.enum(repairRemoteOutcomes);
export type RepairRemoteOutcome = z.infer<typeof repairRemoteOutcomeSchema>;

export const repairEvidenceSchema = z.object({
  ref: z.string().trim().min(1).max(200),
  revision: z.number().int().min(1),
  recordedState: z.literal("unknown"),
  remoteOutcome: repairRemoteOutcomeSchema,
  observedAt: z.string().datetime(),
});
export type RepairEvidence = z.infer<typeof repairEvidenceSchema>;

export const repairSchema = z.object({
  id: z.uuid(),
  proposalId: z.uuid().nullable(),
  runId: z.uuid(),
  tenantId: z.uuid().nullable(),
  kind: z.string().min(1),
  ageSeconds: z.number().int().min(0),
  revision: z.number().int().min(0),
  creditsAtRisk: z.number().int().min(0),
  recordedState: z.string().min(1),
  remoteOutcome: repairRemoteOutcomeSchema.nullable(),
  evidenceRef: z.string().min(1).nullable(),
  observedAt: z.string().datetime().nullable(),
  evidenceRequired: z.boolean(),
  resolutions: z.array(repairResolutionSchema),
  resolution: repairResolutionSchema.nullable(),
  state: repairStateSchema,
  proposedBy: z.uuid().nullable(),
  authorisedBy: z.uuid().nullable(),
});
export type Repair = z.infer<typeof repairSchema>;

export const repairListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type RepairListQuery = z.infer<typeof repairListQuerySchema>;

export const repairListSchema = z.object({
  repairs: z.array(repairSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
  hasMore: z.boolean(),
});
export type RepairList = z.infer<typeof repairListSchema>;

/**
 * `POST /api/v1/operator/repairs/:id`. Where credits are at risk a second operator must
 * authorise, so the authorisation is explicit and cannot be the proposer.
 */
export const proposeRepairSchema = z.object({
  resolution: repairResolutionSchema,
  revision: z.number().int().min(1),
  evidenceRef: z.string().trim().min(1).max(200),
  // A second operator authorises through their own authenticated request.
  authorisedBy: z.null().optional(),
});
export type ProposeRepair = z.infer<typeof proposeRepairSchema>;

/** Authorisation is bound to the exact current repair revision; identity comes from the session. */
export const authoriseRepairSchema = z.object({ revision: z.number().int().min(1) }).strict();
export type AuthoriseRepair = z.infer<typeof authoriseRepairSchema>;

/** Kinds of governance item, one queue for everything an operator must decide. */
export const governanceKinds = [
  "flagged-grant",
  "credit-adjustment",
  "tool-profile",
  "marketplace-item",
  "repair",
] as const;
export const governanceKindSchema = z.enum(governanceKinds);
export type GovernanceKind = z.infer<typeof governanceKindSchema>;

export const governanceStates = [
  "open",
  "released",
  "reduced",
  "denied",
  "approved",
  "rejected",
] as const;
export const governanceStateSchema = z.enum(governanceStates);
export type GovernanceState = z.infer<typeof governanceStateSchema>;

export const governanceItemSchema = z.object({
  id: z.uuid(),
  kind: governanceKindSchema,
  subjectRef: z.string().min(1),
  requestedBy: z.uuid().nullable(),
  waitingSeconds: z.number().int().min(0),
  state: governanceStateSchema,
  decidedBy: z.uuid().nullable(),
  reason: z.string().nullable(),
  canCallerDecide: z.boolean(),
  requestedCredits: z.number().int().min(1).nullable(),
  signals: z.string().nullable(),
});
export type GovernanceItem = z.infer<typeof governanceItemSchema>;

export const governanceListSchema = z.array(governanceItemSchema);
export type GovernanceList = z.infer<typeof governanceListSchema>;

/**
 * `POST /api/v1/operator/governance/:id/decide`. The decider is never the requester, so the
 * caller's own proposals are not decidable and return 403. A reason is required to reduce or
 * deny a grant.
 */
export const decideGovernanceSchema = z
  .object({
    decision: z.enum(["release", "reduce", "deny", "approve", "reject"]),
    reducedCredits: z.number().int().min(0).nullable().optional(),
    reason: z.string().trim().max(500).nullable().optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.decision === "reduce" && value.reducedCredits === undefined) {
      context.addIssue({
        code: "custom",
        message: "Reduced credits are required for a reduced grant.",
        path: ["reducedCredits"],
      });
    }
  });
export type DecideGovernance = z.infer<typeof decideGovernanceSchema>;

/** Catalogue item kinds and their evaluation states. The hub extension tables own extension packs; this catalogue accepts tools and skills only. */
export const catalogueKinds = ["tool", "skill"] as const;
export const catalogueKindSchema = z.enum(catalogueKinds);
export type CatalogueKind = z.infer<typeof catalogueKindSchema>;

/** Nothing enters the catalogue without a passing evaluation. */
export const evaluationStates = ["passed", "failed", "pending"] as const;
export const evaluationStateSchema = z.enum(evaluationStates);
export type EvaluationState = z.infer<typeof evaluationStateSchema>;
export const catalogueEvaluationResultSchema = z
  .object({
    state: evaluationStateSchema,
    reference: z.string().trim().min(1).max(300),
    /**
     * The content digest the evaluation reviewed. A passing skill evaluation pins exactly
     * the bytes it approved, so an install can only become marketplace when its digest
     * matches this one. The operator caller never supplies it: only the evaluation does.
     */
    contentDigest: contentDigestSchema.optional(),
    /**
     * The permission list the evaluation read from the reviewed manifest. Only the
     * evaluation supplies it; the catalogue stores it verbatim and installs copy it, so
     * the drawer never infers permissions from version, source, digest, or status.
     */
    permissions: z.array(z.string().trim().min(1).max(200)).max(100).optional(),
  })
  .strict();
export type CatalogueEvaluationResult = z.infer<typeof catalogueEvaluationResultSchema>;

export const catalogueItemSchema = z.object({
  id: z.uuid(),
  kind: catalogueKindSchema,
  name: z.string().min(1),
  version: z.string().min(1),
  source: z.string().min(1),
  evaluation: evaluationStateSchema,
  evaluationRef: z.string().min(1),
  /**
   * The server-verified content digest pinned by the passing evaluation. Present on skill
   * rows published after digest pinning; absent on older rows and tool rows.
   */
  contentDigest: contentDigestSchema.optional(),
  /**
   * The permission list the passing evaluation read from the reviewed manifest. Present
   * only when the evaluation supplied one; absent means no reviewed permission list is
   * available, and no list is ever inferred from version, source, digest, or status.
   */
  permissions: z.array(z.string().min(1)).optional(),
  projectsUsing: z.number().int().min(0),
});
export type CatalogueItem = z.infer<typeof catalogueItemSchema>;

export const catalogueListSchema = z.array(catalogueItemSchema);
export type CatalogueList = z.infer<typeof catalogueListSchema>;

/**
 * `POST /api/v1/operator/catalogue` adds an item once its evaluation has passed. A version
 * already in use is never edited in place, so an add is always a new version. Extension packs
 * live in the hub extension tables and never enter this catalogue.
 */
export const addCatalogueItemSchema = z
  .object({
    kind: catalogueKindSchema,
    name: z.string().trim().min(1).max(200),
    version: z.string().trim().min(1).max(60),
    source: z.string().trim().min(1).max(300),
  })
  .strict();
export type AddCatalogueItem = z.infer<typeof addCatalogueItemSchema>;

export const creditsPolicyValuesSchema = z
  .object({
    welcomeGrantCredits: z.number().int().min(0),
    welcomeGrantExpiryDays: z.number().int().min(0),
    grantsPerUser: z.number().int().min(0),
    velocityHoldProjects: z.number().int().min(0),
    velocityHoldWindowHours: z.number().int().min(0),
    packPrices: z.array(
      z.object({
        pack: creditPackSchema,
        credits: z.number().int().min(1),
        priceCents: z.number().int().min(1),
        currency: z.literal("USD"),
      }),
    ),
  })
  .strict()
  .superRefine(({ packPrices }, context) => {
    const packs = packPrices.map((item) => item.pack);
    if (new Set(packs).size !== packs.length) {
      context.addIssue({
        code: "custom",
        message: "Each credit tier may appear only once.",
        path: ["packPrices"],
      });
    }
  });
export type CreditsPolicyValues = z.infer<typeof creditsPolicyValuesSchema>;

/**
 * Credit policy defaults that apply to every wallet. A change applies to new grants and
 * purchases only, never to credits already issued.
 */
export const creditsPolicySchema = z.object({
  ...creditsPolicyValuesSchema.shape,
  pendingChange: z
    .object({
      id: z.uuid(),
      policy: creditsPolicyValuesSchema,
      proposedBy: z.uuid(),
      state: z.enum(["proposed", "authorised", "rejected"]),
    })
    .nullable(),
  paymentProviderHealth: dependencyStateSchema,
});
export type CreditsPolicy = z.infer<typeof creditsPolicySchema>;

/** Product defaults. Persisted policy changes are read only by new grant and purchase flows. */
export const DEFAULT_CREDITS_POLICY: CreditsPolicyValues = {
  welcomeGrantCredits: 2_000,
  welcomeGrantExpiryDays: 14,
  grantsPerUser: 3,
  velocityHoldProjects: 3,
  velocityHoldWindowHours: 24,
  packPrices: creditPacks.map((pack) => ({
    pack,
    ...CREDIT_PACKS[pack],
    currency: "USD" as const,
  })),
};

/** `PUT /api/v1/operator/credits-policy` proposes a change. A second operator must authorise. */
export const proposeCreditsPolicySchema = z
  .object({
    policy: creditsPolicyValuesSchema,
    // A second operator authorises through their own authenticated request.
    authorisedBy: z.null().optional(),
  })
  .strict();
export type ProposeCreditsPolicy = z.infer<typeof proposeCreditsPolicySchema>;

export const authoriseCreditsPolicySchema = z.object({ id: z.uuid() }).strict();
export type AuthoriseCreditsPolicy = z.infer<typeof authoriseCreditsPolicySchema>;

/** Drift per component against the signed release manifest. */
export const driftStates = ["matching", "behind", "different", "unknown"] as const;
export const driftStateSchema = z.enum(driftStates);
export type DriftState = z.infer<typeof driftStateSchema>;

export const driftSchema = z.object({
  component: z.string().min(1),
  running: z.string().min(1),
  expected: z.string().min(1),
  state: driftStateSchema,
});
export type Drift = z.infer<typeof driftSchema>;

export const driftListSchema = z.array(driftSchema);
export type DriftList = z.infer<typeof driftListSchema>;

export const releaseManifestStates = ["verified", "invalid", "unknown"] as const;
export const releaseManifestStateSchema = z.enum(releaseManifestStates);
export type ReleaseManifestState = z.infer<typeof releaseManifestStateSchema>;

export const operatorDriftReportSchema = z.object({
  components: driftListSchema,
  manifestState: releaseManifestStateSchema,
  reconcilerQueueDepth: z.number().int().min(0).nullable(),
  ledgerToInvoiceVariance: z.number().int().nullable(),
});
export type OperatorDriftReport = z.infer<typeof operatorDriftReportSchema>;

/** Trusted runtime inputs for comparing a verified release manifest with deployed components. */
export const operatorDriftSnapshotSchema = z.object({
  manifest: z
    .object({
      signatureVerified: z.boolean(),
      components: z.array(z.object({ component: z.string().min(1), version: z.string().min(1) })),
    })
    .nullable(),
  runningComponents: z
    .array(z.object({ component: z.string().min(1), version: z.string().min(1) }))
    .nullable(),
  reconcilerQueueDepth: z.number().int().min(0).nullable(),
  ledgerToInvoiceVariance: z.number().int().nullable(),
});
export type OperatorDriftSnapshot = z.infer<typeof operatorDriftSnapshotSchema>;

/**
 * The operator audit log. Reading and exporting are themselves recorded, so an entry carries
 * who initiated it, who decided it, and what carried it out.
 */
export const operatorAuditSchema = z.object({
  id: z.uuid(),
  occurredAt: z.string().datetime(),
  action: z.string().min(1),
  tenantId: z.uuid().nullable(),
  initiatedBy: z.uuid().nullable(),
  decidedBy: z.uuid().nullable(),
  carriedOutBy: z.string().min(1),
  detail: z.string().nullable(),
});
export type OperatorAuditEntry = z.infer<typeof operatorAuditSchema>;

export const operatorAuditListSchema = z.array(operatorAuditSchema);
export type OperatorAuditList = z.infer<typeof operatorAuditListSchema>;

export const operatorAuditQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  action: z.string().trim().min(1).max(120).optional(),
  tenantId: z.uuid().optional(),
});
export type OperatorAuditQuery = z.infer<typeof operatorAuditQuerySchema>;

export const operatorAuditPageSchema = z.object({
  entries: operatorAuditListSchema,
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
  total: z.number().int().min(0),
});
export type OperatorAuditPage = z.infer<typeof operatorAuditPageSchema>;

export const operatorAccessResponseSchema = z.object({
  operators: operatorListSchema,
  servicePrincipals: servicePrincipalListSchema,
});
export type OperatorAccessResponse = z.infer<typeof operatorAccessResponseSchema>;

/**
 * Funnel conversion stages and pilot cohort metrics.
 * Tracks the four critical lifecycle milestones: signup, first run, first PR, and paid conversion.
 */
export const funnelEventNames = ["signup", "first_run", "first_pr", "paid"] as const;
export const funnelEventNameSchema = z.enum(funnelEventNames);
export type FunnelEventName = z.infer<typeof funnelEventNameSchema>;

export const funnelStageMetricsSchema = z.object({
  stage: funnelEventNameSchema,
  count: z.number().int().min(0),
  conversionRate: z.number().min(0).max(1),
  dropOffRate: z.number().min(0).max(1),
});
export type FunnelStageMetrics = z.infer<typeof funnelStageMetricsSchema>;

export const operatorFunnelReportSchema = z.object({
  cohort: z.string().min(1),
  stages: z.array(funnelStageMetricsSchema),
  overallConversionRate: z.number().min(0).max(1),
  generatedAt: z.string().datetime(),
});
export type OperatorFunnelReport = z.infer<typeof operatorFunnelReportSchema>;
